import {Alert, Button, Card, openDialog} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {TelephonyLineTwilioNumber} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {GsmForwardingCodesDialog} from './GsmForwardingCodesDialog';

interface TelephonyLineNumberRowProps extends RequiredTestIdProps {
  number: TelephonyLineTwilioNumber;
}

const getLookupAlertMessage = (
  lookupStatus: TelephonyLineTwilioNumber['lookupStatus']
): string | Nullish =>
  match(lookupStatus)
    .with('ok', () => null)
    .with('missing', () => i18n.t('page.settings.telephony.numberMissingAtProvider'))
    .with('unavailable', () => i18n.t('page.settings.telephony.numberDataUnavailable'))
    .exhaustive();

export function TelephonyLineNumberRow(props: TelephonyLineNumberRowProps) {
  const lookupAlertMessage = getLookupAlertMessage(props.number.lookupStatus);

  /**
   * A number that is still ours but has never taken a call through Omnetic is unproven: its voice
   * URL may point nowhere. A `missing` number already says the number is gone, so it needs no
   * second alert.
   */
  const isAwaitingFirstCall =
    props.number.lookupStatus !== 'missing' && isNil(props.number.lastInboundCallAt);

  /**
   * A `missing` number is gone from the provider account, so nothing answers it and the codes
   * would point the dealer's handset at a destination that cannot take the call. `unavailable`
   * only means the provider was unreachable when we asked — the number is still ours and the codes
   * are formatted from the E.164 we already hold, so they stay available.
   */
  const isForwardingAvailable = props.number.lookupStatus !== 'missing';

  const handleShowForwardingCodes = () =>
    openDialog(
      <GsmForwardingCodesDialog
        phoneNumber={props.number.phoneNumber}
        data-testid={suffixTestId('forwardingCodes', props)}
      />,
      {
        id: 'gsmForwardingCodesDialog',
        title: i18n.t('page.settings.telephony.forwardingCodesTitle', {
          phoneNumber: props.number.phoneNumber,
        }),
        size: 'small',
      }
    );

  return (
    <Card variant="inlineWhite" data-testid={suffixTestId('numberRow', props)}>
      <VStack spacing={3}>
        <HStack justify="space-between" align="center" spacing={2}>
          <Text size="base" alternative data-testid={suffixTestId('phoneNumber', props)}>
            {props.number.phoneNumber}
          </Text>
          <Show when={isForwardingAvailable}>
            <Button
              variant="link"
              title={i18n.t('page.settings.telephony.forwardingCodes')}
              onClick={handleShowForwardingCodes}
              data-testid={suffixTestId('showForwardingCodes', props)}
            />
          </Show>
        </HStack>
        <Text size="xSmall" color="tertiary" data-testid={suffixTestId('sid', props)}>
          {props.number.sid}
        </Text>
        <Show when={lookupAlertMessage}>
          <Alert
            variant={props.number.lookupStatus === 'missing' ? 'warning' : 'info'}
            message={lookupAlertMessage ?? ''}
            data-testid={suffixTestId('lookupAlert', props)}
          />
        </Show>
        <Show when={isAwaitingFirstCall}>
          <Alert
            variant="warning"
            message={i18n.t('page.settings.telephony.numberAwaitingFirstCall')}
            data-testid={suffixTestId('awaitingFirstCall', props)}
          />
        </Show>
      </VStack>
    </Card>
  );
}
